import React from 'react';
import { Box, Chip, IconButton, Stack, Typography } from '@mui/material';
import { Armour, MemberRole, TreasureItem, Weapon } from '../../types';
import { PopoverItem } from '../../hooks/useItemPopover';
import { formatDamageModifier } from '../../utils/formatting';
import { useReferenceDataStore } from '../../stores/referenceDataStore';
import { useWarbandStore } from '../../stores/warbandStore';
import { MemberEquipmentEditor } from './MemberEquipmentEditor';

interface MemberWeaponsProps {
  weapons: Weapon[];
  armours?: Armour[];
  magicItems?: TreasureItem[];
  editMode: boolean;
  memberRole: MemberRole;
  memberId: string;
  memberName: string;
  onItemClick: (event: React.MouseEvent<HTMLElement>, item: PopoverItem) => void;
  onSaveWeapons: (weaponIds: string[]) => void;
  onSaveArmours?: (armourIds: string[]) => void;
  onSaveMagicItems?: (itemIds: string[]) => void;
  sectionTitle?: string;
}

export const MemberWeapons: React.FC<MemberWeaponsProps> = ({
  weapons,
  armours = [],
  magicItems = [],
  editMode,
  memberRole,
  memberId,
  memberName,
  onItemClick,
  onSaveWeapons,
  onSaveArmours,
  onSaveMagicItems,
  sectionTitle = 'Weapons',
}) => {
  const allWeapons = useReferenceDataStore((state) => state.weapons);
  const vaultItems = useWarbandStore((state) => state.vaultItems);
  const wizard = useWarbandStore((state) => state.wizardData);
  const apprentice = useWarbandStore((state) => state.apprenticeData);
  const captain = useWarbandStore((state) => state.captainData);
  const soldiers = useWarbandStore((state) => state.soldiersData?.soldiers);
  const memberNames: Record<string, string> = {};
  for (const member of [wizard, apprentice, captain, ...(soldiers ?? [])]) {
    if (member) memberNames[member.id] = member.name;
  }
  const isGrimoire = (itemId: string) => itemId.toLowerCase().startsWith('grimoire_');
  const availableMagicItems = vaultItems.filter((item) => !isGrimoire(item.id));
  const assignedMagicItems = magicItems.filter((item) => !isGrimoire(item.id));
  const canEdit = editMode && memberRole !== 'soldier';

  return <>
      <MemberEquipmentEditor
        title={sectionTitle}
        editMode={canEdit}
        modalMemberName={memberName}
        modalAvailableItems={[
          ...allWeapons.map((weapon) => ({ id: weapon.id, name: weapon.name, category: 'Weapon', description: weapon.description })),
          ...armours.map((armour) => ({ id: armour.id, name: armour.name, category: 'Armour', description: armour.description })),
          ...availableMagicItems.map((item) => ({ id: item.id, name: item.name, category: 'Magic', description: item.description, disabled: Boolean(item.warbandMemberId && item.warbandMemberId !== memberId), disabledReason: item.warbandMemberId && item.warbandMemberId !== memberId ? `Assigned to ${memberNames[item.warbandMemberId] ?? 'another member'}` : undefined })),
        ]}
        onModalChange={(selectedIds) => {
          onSaveWeapons(selectedIds.filter((id) => allWeapons.some((weapon) => weapon.id === id)));
          onSaveArmours?.(selectedIds.filter((id) => armours.some((armour) => armour.id === id)));
          onSaveMagicItems?.([
            ...magicItems.filter((item) => isGrimoire(item.id)).map((item) => item.id),
            ...selectedIds.filter((id) => assignedMagicItems.some((item) => item.id === id) || availableMagicItems.some((item) => item.id === id)),
          ]);
        }}
        items={[...weapons.map((weapon, idx) => ({
          id: weapon.id,
          name: formatDamageModifier(weapon.name, weapon.damageModifier),
          onClick: (event: React.MouseEvent<HTMLElement>) => onItemClick(event, weapon),
          onRemove: canEdit ? () => onSaveWeapons(weapons.filter((_, i) => i !== idx).map((item) => item.id)) : undefined,
        })), ...armours.map((armour) => ({
          id: armour.id,
          name: armour.name,
          onClick: (event: React.MouseEvent<HTMLElement>) => onItemClick(event, armour),
          onRemove: canEdit ? () => onSaveArmours?.(armours.filter((item) => item.id !== armour.id).map((item) => item.id)) : undefined,
        })), ...assignedMagicItems.map((item) => ({
          id: `magic-${item.id}`,
          name: item.name,
          onClick: (event: React.MouseEvent<HTMLElement>) => onItemClick(event, { id: item.id, name: item.name, description: item.description, specialTags: [] }),
          onRemove: canEdit ? () => onSaveMagicItems?.(magicItems.filter((current) => current.id !== item.id).map((current) => current.id)) : undefined,
        }))]}
        modalSelectedItems={[...weapons.map((weapon) => ({ id: weapon.id, name: weapon.name })), ...armours.map((armour) => ({ id: armour.id, name: armour.name })), ...assignedMagicItems.map((item) => ({ id: item.id, name: item.name }))]}
      />

  </>;
};
